"use client";

import { useEffect, useState } from "react";
import { getCookie, deleteCookie } from "@repo/client-cookies";
import { Link } from "@repo/next-utils/router";
import { Button } from "@repo/ui/button";
import * as Sheet from "@repo/ui/sheet";
import { getSessionStorage, setSessionStorage } from "@repo/utils/storage";
import { setupConsentBannerClosedListener } from "@/components/consent";
import {
  GEO_REDIRECT_DISMISSED_KEY,
  LEGACY_NON_UK_NOTIFICATION_COOKIE,
  ASOS_BASE_URL,
} from "@/constants/static-constants";
import { CloseIcon } from "@/theme/icons/close";
import {
  TYPOGRAPHY_LABEL3,
  TYPOGRAPHY_BODY3_SENTENCE,
  TYPOGRAPHY_HEADING3_BOLD,
} from "@/theme/topshop/typography/typography-constants";
import {
  getRedirectType,
  getEuRedirectUrl,
  type RedirectType,
} from "@/utils/geo-redirect";
import { getCustomerGeoCountry } from "@/utils/get-customer-geo-country";
import { useSmallViewport } from "../viewport";
import classes from "./geo-redirect-popup.module.css";

export const GeoRedirectPopup = () => {
  const [isOpen, setIsOpen] = useState(false);
  const [redirectType, setRedirectType] = useState<RedirectType>(null);
  const [redirectUrl, setRedirectUrl] = useState<string>("");
  const isMobile = useSmallViewport();

  useEffect(() => {
    const cleanup = setupConsentBannerClosedListener(() => {
      const legacyCookie = getCookie(LEGACY_NON_UK_NOTIFICATION_COOKIE);
      const dismissed = getSessionStorage<boolean>(GEO_REDIRECT_DISMISSED_KEY);

      if (legacyCookie) {
        deleteCookie(LEGACY_NON_UK_NOTIFICATION_COOKIE);
      }

      if (dismissed) {
        return;
      }

      const geoCountry = getCustomerGeoCountry();
      const type = getRedirectType(geoCountry);

      if (!type) {
        return;
      }

      setRedirectType(type);

      if (type === "EU") {
        setRedirectUrl(getEuRedirectUrl(geoCountry || ""));
      } else {
        const countryPath = geoCountry
          ? `/${geoCountry.toLowerCase()}/topshop/`
          : "/topshop/";

        setRedirectUrl(`${ASOS_BASE_URL}${countryPath}`);
      }

      setIsOpen(true);
    });

    return cleanup;
  }, []);

  const handleDismiss = () => {
    setSessionStorage(GEO_REDIRECT_DISMISSED_KEY, true);
    setIsOpen(false);
  };

  if (!isOpen || !redirectType) {
    return null;
  }

  return (
    <Sheet.Root open={isOpen}>
      <Sheet.Content position={isMobile ? "bottom" : "center"} overlayAnimation>
        <div className={classes.dialogueBox}>
          <Sheet.Close className={classes.closeButton} onClick={handleDismiss}>
            <CloseIcon label="Close redirect popup" />
          </Sheet.Close>
          <div className={classes.bodyContent}>
            <div className={classes.header}>
              <Sheet.Title className={TYPOGRAPHY_HEADING3_BOLD}>
                {redirectType === "EU"
                  ? "You're browsing from an EU country"
                  : "You're browsing from outside the UK"}
              </Sheet.Title>
            </div>
            <Sheet.Description className={classes.description}>
              <p className={TYPOGRAPHY_BODY3_SENTENCE}>
                {redirectType === "EU" ? (
                  <>
                    Head to EU.TOPSHOP.COM to shop Topshop with local pricing
                    and shipping options.
                  </>
                ) : (
                  <>
                    We currently only ship to the UK. Head to ASOS.COM to shop
                    Topshop and Topman with local pricing and shipping options.
                  </>
                )}
              </p>
            </Sheet.Description>
          </div>
          <div className={classes.ctas}>
            <Button
              variant="primary"
              className={TYPOGRAPHY_LABEL3}
              alignment="start"
              size="large"
              fullWidth
              asChild
            >
              <Link href={redirectUrl}>
                {redirectType === "EU"
                  ? "SHOP ON EU.TOPSHOP.COM"
                  : "SHOP ON ASOS.COM"}
              </Link>
            </Button>
            <Button
              variant="secondary"
              className={`${TYPOGRAPHY_LABEL3} ${classes.dismissButton}`}
              alignment="start"
              size="large"
              fullWidth
              onClick={handleDismiss}
            >
              CONTINUE ON UK SITE
            </Button>
          </div>
        </div>
      </Sheet.Content>
    </Sheet.Root>
  );
};
